/**
 * =============================================================================
 * COMPONENT:  AdsDecisions.js
 * FUNKTION:   Entscheidungs-Notizen pro Bereich
 *             Freitags erfassen → Montags umsetzen → nächsten Freitag prüfen
 * VERSION:    v1.0
 * DATUM:      2026-03-04
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AdsDecisions.css';

const API = '/api/ads_decisions.php';
function CharCount({ value, max }) {
    const len = (value || '').length;
    const pct = len / max;
    const cls = pct >= 1 ? 'cc-over' : pct >= 0.8 ? 'cc-warn' : 'cc-ok';
    return <span className={`char-count ${cls}`}>{len}/{max}</span>;
}

const STATUS_LIST = [
    { key: 'offen',      label: 'O',   emoji: '📋', cls: 'status-offen' },
    { key: 'in_arbeit',  label: 'I',  emoji: '⚙️', cls: 'status-in_arbeit' },
    { key: 'beobachten', label: 'B',   emoji: '👁️', cls: 'status-beobachten' },
    { key: 'pausiert',   label: 'P',   emoji: '⏸️', cls: 'status-pausiert' },
    { key: 'umgesetzt',  label: 'U',  emoji: '✅', cls: 'status-umgesetzt' },
    { key: 'geprueft',   label: 'G', emoji: '🔍', cls: 'status-geprueft' },
    { key: 'archiv',     label: 'A',  emoji: '📦', cls: 'status-archiv' },
];
const STATUS_LABELS = Object.fromEntries(STATUS_LIST.map(s => [s.key, s]));
const BEREICH_LABELS = {
    kampagnen:   '📊 Kampagnen',
    assets:      '🎯 Assets',
    keywords:    '🔑 Keywords',
    geraete:     '📱 Geräte',
    alter:       '👥 Alter',
    zeitplan:    '🕐 Zeitplan',
    auktionen:   '⚔️ Auktionen',
};

export default function AdsDecisions({ bereich, kampagne = '', kontext = '', selectedIds = [], campaignList = [], campaignId = null }) {
    const [decisions, setDecisions]     = useState([]);
    const [loading, setLoading]         = useState(false);
    const [showForm, setShowForm]       = useState(false);
    const [editId, setEditId]           = useState(null);
    const [expandedId, setExpandedId]   = useState(null);

    const [form, setForm] = useState({
        datum:        new Date().toISOString().split('T')[0],
        kampagne_id:  '',
        beobachtung:  '',
        massnahme:    '',
        wirkung:      '',
        link:         '',
        status:       'offen',
    });

    const token = localStorage.getItem('token') || '';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const cids = campaignId ? [campaignId] : selectedIds;
            if (!cids || cids.length === 0) { setDecisions([]); setLoading(false); return; }
            const url = `${API}?bereich=${bereich}&campaign_ids=${cids.join(',')}&token=${token}`;
            const res = await fetch(url);
            const json = await res.json();
            if (json.success) {
                setDecisions(json.data);
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [bereich, token, campaignId, selectedIds]);

    useEffect(() => { load(); }, [load]);

    const resetForm = () => {
        setForm({
            datum:        new Date().toISOString().split('T')[0],
            kampagne_id:  '',
            beobachtung:  '',
            massnahme:    '',
            wirkung:      '',
            link:         '',
            status:       'offen',
        });
        setEditId(null);
        setShowForm(false);
    };

    const handleSave = async () => {
        if (!form.kampagne_id || !form.beobachtung || !form.massnahme) return;
        try {
            if (editId) {
                await fetch(API, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ id: editId, ...form }),
                });
            } else {
                await fetch(API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ ...form, bereich, campaign_id: campaignId, kampagne: form.kampagne_id, kontext }),
                });
            }
            resetForm();
            load();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Notiz löschen?')) return;
        await fetch(`${API}?id=${id}&token=${token}`, { method: 'DELETE' });
        load();
    };

    const handleEdit = (d) => {
        setForm({
            datum:        d.datum,
            kampagne_id:  d.kampagne || '',
            beobachtung:  d.beobachtung,
            massnahme:    d.massnahme,
            wirkung:      d.wirkung || '',
            link:         d.link || '',
            status:       d.status,
        });
        setEditId(d.id);
        setShowForm(true);
    };

    const handleStatusChange = async (id, newStatus) => {
        await fetch(API, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ id, status: newStatus }),
        });
        load();
    };

    const offene      = decisions.filter(d => d.status === 'offen');
    const beobachten  = decisions.filter(d => d.status === 'beobachten');
    const umgesetzt   = decisions.filter(d => d.status === 'umgesetzt');
    const geprueft    = decisions.filter(d => d.status === 'geprueft');

    return (
        <div className="decisions-wrap">
            {/* Header */}
            <div className="decisions-header">
                <div className="decisions-title">
                    <span>📝 Entscheidungen</span>
                    {decisions.length > 0 && (
                        <div className="decisions-counts">
                            {offene.length > 0      && <span className="dc-badge dc-offen">{offene.length} offen</span>}
                                            {beobachten.length > 0  && <span className="dc-badge dc-beobachten">{beobachten.length} beobachten</span>}
                            {umgesetzt.length > 0 && <span className="dc-badge dc-umgesetzt">{umgesetzt.length} umgesetzt</span>}
                            {geprueft.length > 0  && <span className="dc-badge dc-geprueft">{geprueft.length} geprüft</span>}
                        </div>
                    )}
                </div>
                <button className="decisions-add-btn" onClick={() => { setShowForm(!showForm); setEditId(null); }}>
                    {showForm ? '✕ Abbrechen' : '+ Neue Notiz'}
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="decisions-form">
                    <div className="df-row">
                        <div className="df-field">
                            <label>Datum</label>
                            <input type="date" value={form.datum}
                                onChange={e => setForm({ ...form, datum: e.target.value })} />
                        </div>
                        <div className="df-field">
                            <label>Kampagne *</label>
                            <select value={form.kampagne_id}
                                onChange={e => setForm({ ...form, kampagne_id: e.target.value })}>
                                <option value="">— bitte wählen —</option>
                                {campaignList
                                    .filter(c => selectedIds.includes(String(c.campaign_id)))
                                    .map(c => (
                                        <option key={c.campaign_id} value={String(c.campaign_id)}>
                                            {c.campaign_name}
                                        </option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="df-field df-field-wide">
                            <label>Beobachtung * <CharCount value={form.beobachtung} max={200} /></label>
                            <input type="text" placeholder="z.B. Desktop: 1 Conversion, höchster CPC"
                                maxLength={200}
                                value={form.beobachtung}
                                onChange={e => setForm({ ...form, beobachtung: e.target.value })} />
                        </div>
                    </div>
                    <div className="df-row">
                        <div className="df-field df-field-wide">
                            <label>Maßnahme * <CharCount value={form.massnahme} max={200} /></label>
                            <input type="text" placeholder="z.B. Desktop Gebot +20% am Montag"
                                maxLength={200}
                                value={form.massnahme}
                                onChange={e => setForm({ ...form, massnahme: e.target.value })} />
                        </div>
                        <div className="df-field df-field-wide">
                            <label>Link (optional)</label>
                            <input type="url" placeholder="https://..."
                                maxLength={500}
                                value={form.link}
                                onChange={e => setForm({ ...form, link: e.target.value })} />
                        </div>
                        {editId && (
                            <div className="df-field">
                                <label>Status</label>
                                <select value={form.status}
                                    onChange={e => setForm({ ...form, status: e.target.value })}>
                                    <option value="offen">📋 Offen</option>
                                    <option value="umgesetzt">✅ Umgesetzt</option>
                                    <option value="geprueft">🔍 Geprüft</option>
                                </select>
                            </div>
                        )}
                    </div>
                    {editId && (
                        <div className="df-row">
                            <div className="df-field df-field-wide">
                                <label>Wirkung (nach Umsetzung) <CharCount value={form.wirkung} max={300} /></label>
                                <input type="text" placeholder="z.B. Conversions +2, CPA gesunken"
                                    maxLength={300}
                                    value={form.wirkung}
                                    onChange={e => setForm({ ...form, wirkung: e.target.value })} />
                            </div>
                        </div>
                    )}
                    <div className="df-actions">
                        <button className="df-save-btn" onClick={handleSave}>
                            {editId ? '💾 Speichern' : '+ Anlegen'}
                        </button>
                        <button className="df-cancel-btn" onClick={resetForm}>Abbrechen</button>
                    </div>
                </div>
            )}

            {/* List */}
            {loading && <p className="decisions-loading">Lädt…</p>}

            {!loading && decisions.length === 0 && (
                <p className="decisions-empty">Noch keine Notizen für diesen Bereich.</p>
            )}

            {!loading && decisions.length > 0 && (
                <div className="decisions-list">
                    {decisions.map(d => {
                        const st = STATUS_LABELS[d.status] || STATUS_LABELS.offen;
                        const expanded = expandedId === d.id;
                        return (
                            <div key={d.id} className={`decision-card ${st.cls} ${expanded ? 'expanded' : ''}`}>
                                <div className="dc-top" onClick={() => setExpandedId(expanded ? null : d.id)}>
                                    <div className="dc-left">
                                        <span className={`dc-status ${st.cls}`}>{st.emoji} {st.label}</span>
                                        <span className="dc-datum">{d.datum}</span>
                                        {d.kampagne && (() => {
                                            const c = campaignList.find(x => String(x.campaign_id) === d.kampagne);
                                            return <span className="dc-kontext">{c ? c.campaign_name : d.kampagne}</span>;
                                        })()}
                                    </div>
                                    <div className="dc-beobachtung">{d.beobachtung}</div>
                                    <span className="dc-toggle">{expanded ? '▲' : '▼'}</span>
                                </div>

                                {expanded && (
                                    <div className="dc-body">
                                        <div className="dc-row">
                                            <span className="dc-label">Maßnahme</span>
                                            <span className="dc-value dc-massnahme">{d.massnahme}</span>
                                        </div>
                                        {d.wirkung && (
                                            <div className="dc-row">
                                                <span className="dc-label">Wirkung</span>
                                                <span className="dc-value">{d.wirkung}</span>
                                            </div>
                                        )}
                                        {d.link && (
                                            <div className="dc-row">
                                                <span className="dc-label">Link</span>
                                                <a className="dc-value dc-link" href={d.link} target="_blank" rel="noreferrer">{d.link}</a>
                                            </div>
                                        )}
                                        <div className="dc-footer">
                                            <div className="dc-status-btns">
                                                {Object.entries(STATUS_LABELS).map(([key, val]) => (
                                                    <button key={key}
                                                        className={`dc-status-btn ${d.status === key ? 'active' : ''}`}
                                                        onClick={() => handleStatusChange(d.id, key)}>
                                                        {val.emoji} {val.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="dc-edit-btns">
                                                <button className="dc-edit" onClick={() => handleEdit(d)}>✏️ Bearbeiten</button>
                                                <button className="dc-delete" onClick={() => handleDelete(d.id)}>🗑️ Löschen</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}